/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { conformToMask } from 'react-text-mask';
import { compare } from 'fast-json-patch';
import isEqual from 'lodash/isEqual';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import showServerError from 'helpers/showError';
import { MASK } from 'components/Shared/FormattedPhone';
import Loader from 'components/Shared/Loader';
import { ACCOUNT_TYPES } from 'components/Accounts/constants';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../redux_store/reducer/reducers/notificationsReducer';
import { setMetadata } from '../../../redux_store/reducer/reducers/userReducer/userReducer';
import { AccountSchema, formattedAccountsValues } from '../helpers';
import AccountForm from './AccountForm/AccountForm';
import { getTransitionWithVariable } from '../../../redux_store/reducer/reducers/i18nReducer';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const AccountEdit = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { accountUuid } = useParams();
  const { en: { btn, errors, notifications, forms, titles } } = useSelector((state) => state.i18n);
  const [backendErrors, setBackendErrors] = useState({});
  const [account, setAccount] = useState();
  const {
    user,
    metadata,
    metadata: { currentAccount },
  } = useSelector((state) => state.user);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid || currentAccount.uuid}`,
    },
    false,
  ]);

  // Fetch surveys and resources data once when page loads
  const { payload: surveysPayload, loading: surveysLoading } = useQuery({
    method: 'GET',
    endpoint: '/surveys',
  });

  const { payload: resourcesPayload, loading: resourcesLoading } = useQuery({
    method: 'GET',
    endpoint: '/resources',
  });

  useEffect(() => {
    query().then(({ error, payload }) => {
      if (!error) {
        const data = { ...payload };
        if (data.ispConfiguration
          && data.ispConfiguration.services
          && data.ispConfiguration.services.length) {
          data.ispConfiguration.services = data.ispConfiguration.services
            .map(({ cptCode, price }) => ({
              cptCode: cptCode,
              price: Number(price).toFixed(2).toString(),
            }));
        }

        setAccount(data);
      }
    });
  }, [query]);

  const accountEditAction = (patches) => ({
    method: 'PATCH',
    endpoint: `/accounts/${accountUuid || currentAccount.uuid}`,
    body: patches,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [
    accountEditAction,
  ]);

  const editAccount = async (values, { setSubmitting }) => {
    let accountCopy = JSON.parse(JSON.stringify(account));
    let formattedValues = formattedAccountsValues(JSON.parse(JSON.stringify(values)), false, accountCopy);
    delete formattedValues.otherAddresses; // will be patched manually
    delete accountCopy.otherAddresses;

    if (formattedValues
      && formattedValues.accountPermissions
      && formattedValues.accountPermissions.disableOrders
      && accountCopy
      && accountCopy.accountPermissions
      && accountCopy.accountPermissions.hasOwnProperty('disableOrdersAutomations')) {
      formattedValues.accountPermissions.disableOrdersAutomations = accountCopy.accountPermissions.disableOrdersAutomations;
    }

    // compare all the values except deleted ones
    const patches = compare(accountCopy, formattedValues);
    // create a patch for an array manually

    if (!isEqual(account.otherAddresses, values.otherAddresses)) {
      patches.push({
        op: 'replace',
        path: '/otherAddresses',
        value: values.otherAddresses,
      });
    }
    const wasBusinessNameChanged = !isEqual(
      account.businessName,
      values.businessName,
    );

    setBackendErrors({});

    const { payload, error, status } = await mutate(patches);

    if (error) {
      setIsFailed(true);
      setTimeout(() => {
        setIsFailed(false);
      }, 2000);
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      switch (status) {
        case 422: {
          showServerError(dispatch, errors.validationError);
          setBackendErrors(payload.validationErrors);
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload) {
      if (wasBusinessNameChanged) {
        const newAccounts = [...metadata.accounts].map((account) => {
          if (account.uuid === (accountUuid || currentAccount.uuid)) {
            return {
              ...account,
              businessName: values.businessName,
            };
          }
          return account;
        });
        dispatch(
          setMetadata({
            ...metadata,
            accounts: newAccounts,
          }),
        );
      }
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);

      let newEmailConfiguration = {};
      let newSmsConfiguration = {};

      Object.keys(payload.emailConfiguration).forEach(function(key) {
        if (payload.emailConfiguration[key] && payload.emailConfiguration[key].length > 0) {
          newEmailConfiguration[key] = payload.emailConfiguration[key];
        } else if (account.emailConfiguration[key] && account.emailConfiguration[key].length > 0) {
          newEmailConfiguration[key] = account.emailConfiguration[key];
        } else {
          newEmailConfiguration[key] = [];
        }
      });
      Object.keys(payload.smsConfiguration).forEach(function(key) {
        if (payload.smsConfiguration[key] && payload.smsConfiguration[key].length > 0) {
          newSmsConfiguration[key] = payload.smsConfiguration[key];
        } else if (account.smsConfiguration[key] && account.smsConfiguration[key].length > 0) {
          newSmsConfiguration[key] = account.smsConfiguration[key];
        } else {
          newSmsConfiguration[key] = [];
        }
      });

      const newPayload = {
        ...payload,
        emailConfiguration: newEmailConfiguration,
        smsConfiguration: newSmsConfiguration,
      };

      setAccount(newPayload);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.accountEdited, {
            name: formattedValues.businessName,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
    }
    setSubmitting(false);
  };

  const checkIsDefault = (firstKey, secondKey) => {
    return account?.[firstKey]?.[secondKey]?.find((e) => e.local === 'english')?.isDefault ||
      account?.[firstKey]?.[secondKey]?.find((e) => e.local === 'spanish')?.isDefault ||
      account?.[firstKey]?.[secondKey].length === 0;
  };

  const findEmailOrSmsText = (firstKey, secondKey, local) => {
    return account?.[firstKey]?.[secondKey]?.find(
      (e) => e.local === local,
    )?.text;
  };

  return account ? (
    <Formik
      initialValues={{
        ...account,
        phone: conformToMask(account.phone, MASK).conformedValue,
        fax: conformToMask(account.fax, MASK).conformedValue,
        ianaTimeZone: account.ianaTimeZone || 'America/New_York',
        isShippingTheSame: isEqual(
          account.organizationAddress,
          account.shippingAddress,
        ),
        isDistributionSponsor:
          account.accountType === ACCOUNT_TYPES.DISTRIBUTION_SPONSOR,
        accountPermissions: account.accountPermissions,
        allowedSurveys: account.allowedSurveys || [],
        allowedResources: (account.allowedResources || []).filter(elem => !elem.endsWith('_spanish')),
        customTags: account.customTags,

        emailSurveyInvitationEnglish: findEmailOrSmsText('emailConfiguration', 'surveyInvitation', 'english'),
        emailSurveyInvitationSpanish: findEmailOrSmsText('emailConfiguration', 'surveyInvitation', 'spanish'),
        emailSurveyInvitationIsDefault: checkIsDefault('emailConfiguration', 'surveyInvitation'),
        emailResourceInvitationEnglish: findEmailOrSmsText('emailConfiguration', 'resourceInvitation', 'english'),
        emailResourceInvitationSpanish: findEmailOrSmsText('emailConfiguration', 'resourceInvitation', 'spanish'),
        emailResourceInvitationIsDefault: checkIsDefault('emailConfiguration', 'resourceInvitation'),
        emailSurveyReminderEnglish: findEmailOrSmsText('emailConfiguration', 'surveyReminder', 'english'),
        emailSurveyReminderSpanish: findEmailOrSmsText('emailConfiguration', 'surveyReminder', 'spanish'),
        emailSurveyReminderIsDefault: checkIsDefault('emailConfiguration', 'surveyReminder'),
        emailResourceReminderEnglish: findEmailOrSmsText('emailConfiguration', 'resourceReminder', 'english'),
        emailResourceReminderSpanish: findEmailOrSmsText('emailConfiguration', 'resourceReminder', 'spanish'),
        emailResourceReminderIsDefault: checkIsDefault('emailConfiguration', 'resourceReminder'),
        emailSurveyCompletionEnglish: findEmailOrSmsText('emailConfiguration', 'surveyCompletion', 'english'),
        emailSurveyCompletionSpanish: findEmailOrSmsText('emailConfiguration', 'surveyCompletion', 'spanish'),
        emailSurveyCompletionIsDefault: checkIsDefault('emailConfiguration', 'surveyCompletion'),

        smsSurveyInvitationEnglish: findEmailOrSmsText('smsConfiguration', 'surveyInvitation', 'english'),
        smsSurveyInvitationSpanish: findEmailOrSmsText('smsConfiguration', 'surveyInvitation', 'spanish'),
        smsSurveyInvitationIsDefault: checkIsDefault('smsConfiguration', 'surveyInvitation'),
        smsResourceInvitationEnglish: findEmailOrSmsText('smsConfiguration', 'resourceInvitation', 'english'),
        smsResourceInvitationSpanish: findEmailOrSmsText('smsConfiguration', 'resourceInvitation', 'spanish'),
        smsResourceInvitationIsDefault: checkIsDefault('smsConfiguration', 'resourceInvitation'),
        smsSurveyReminderEnglish: findEmailOrSmsText('smsConfiguration', 'surveyReminder', 'english'),
        smsSurveyReminderSpanish: findEmailOrSmsText('smsConfiguration', 'surveyReminder', 'spanish'),
        smsSurveyReminderIsDefault: checkIsDefault('smsConfiguration', 'surveyReminder'),
        smsResourceReminderEnglish: findEmailOrSmsText('smsConfiguration', 'resourceReminder', 'english'),
        smsResourceReminderSpanish: findEmailOrSmsText('smsConfiguration', 'resourceReminder', 'spanish'),
        smsResourceReminderIsDefault: checkIsDefault('smsConfiguration', 'resourceReminder'),
        smsSurveyCompletionEnglish: findEmailOrSmsText('smsConfiguration', 'surveyCompletion', 'english'),
        smsSurveyCompletionSpanish: findEmailOrSmsText('smsConfiguration', 'surveyCompletion', 'spanish'),
        smsSurveyCompletionIsDefault: checkIsDefault('smsConfiguration', 'surveyCompletion'),
        loading: forms.loading,
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      enableReinitialize
      onSubmit={editAccount}
      validationSchema={AccountSchema}
    >
      {({
          values,
          handleSubmit,
          validateForm,
          setErrors,
          validateField,
          errors,
        }) => (
        <ContentWrapper
          titleText={titles.editAccount}
          containerStyle={{
            overflow: 'visible',
            maxHeight: 'calc(100% - 35px)',
            boxSizing: 'border-box',
            paddingBottom: 0,
          }}
        >
          <div className={classes.contentContainer}>
            <AccountForm
              values={values}
              backendErrors={{ ...errors, ...backendErrors }}
              validateForm={validateForm}
              validateField={validateField}
              setErrors={setErrors}
              isEditMode
              accountUuid={accountUuid || currentAccount.uuid}
              setBackendErrors={setBackendErrors}
              setAccount={setAccount}
              sectionClassName={classes.section}
              sectionTitleClassName={classes.sectionTitle}
              surveysPayload={surveysPayload}
              resourcesPayload={resourcesPayload}
              surveysLoading={surveysLoading}
              resourcesLoading={resourcesLoading}
            />
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                type='submit'
                variant='contained'
                color='primary'
                disabled={
                  loading ||
                  values.scheduleDeliverTime === 'invalid_value'
                }
                onClick={handleSubmit}
                isLoading={loading}
                isSuccess={isSuccess}
                isFailed={isFailed}
                className={classes.actionButton}
              >
                {btn.save}
              </RoundedButton>
            </div>
          </div>
        </ContentWrapper>
      )}
    </Formik>
  ) : (
    <ContentWrapper>
      <Loader fullHeight />
    </ContentWrapper>
  );
};

export default AccountEdit;
